/**
 * Redirects hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

// Types
export interface SearchRedirect {
  id: string
  queryMatch: string
  queryMatchType: string | null
  redirectUrl: string
  description: string | null
  suggestedByAi: number | null
  aiConfidence: number | null
  aiReason: string | null
  hitCount: number | null
  lastHitAt: number | null
  enabled: number | null
  createdAt: number
  updatedAt: number
}

export interface RedirectSuggestion {
  query: string
  searchCount: number
  suggestedUrl: string
  confidence: number
  reason: string
}

export interface ZeroResultSearch {
  query: string
  count: number
}

// Fetch all redirects
export function useRedirects() {
  return useQuery({
    queryKey: ['redirects'],
    queryFn: async (): Promise<SearchRedirect[]> => {
      const res = await fetch('/api/redirects', { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch redirects')
      const data = (await res.json()) as { redirects: SearchRedirect[] }
      return data.redirects
    },
  })
}

// Create redirect
export function useCreateRedirect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      queryMatch: string
      queryMatchType?: 'exact' | 'contains' | 'starts_with'
      redirectUrl: string
      description?: string
    }) => {
      const res = await fetch('/api/redirects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      })
      if (!res.ok) throw new Error('Failed to create redirect')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redirects'] })
    },
  })
}

// Update redirect
export function useUpdateRedirect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: {
      id: string
      queryMatch?: string
      queryMatchType?: 'exact' | 'contains' | 'starts_with'
      redirectUrl?: string
      description?: string
      enabled?: boolean
    }) => {
      const res = await fetch(`/api/redirects/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      })
      if (!res.ok) throw new Error('Failed to update redirect')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redirects'] })
    },
  })
}

// Delete redirect
export function useDeleteRedirect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/redirects/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete redirect')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redirects'] })
    },
  })
}

// Get zero-result searches
export function useZeroResultSearches() {
  return useQuery({
    queryKey: ['redirects', 'zero-results'],
    queryFn: async (): Promise<ZeroResultSearch[]> => {
      const res = await fetch('/api/redirects/zero-results?limit=30', { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch zero-result searches')
      const data = (await res.json()) as { searches: ZeroResultSearch[] }
      return data.searches
    },
  })
}

// Generate AI suggestions
export function useGenerateSuggestions() {
  return useMutation({
    mutationFn: async (): Promise<RedirectSuggestion[]> => {
      const res = await fetch('/api/redirects/suggest', {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to generate suggestions')
      const data = (await res.json()) as { suggestions: RedirectSuggestion[] }
      return data.suggestions
    },
  })
}

// Apply AI suggestions
export function useApplySuggestions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (suggestions: RedirectSuggestion[]) => {
      const res = await fetch('/api/redirects/apply-suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ suggestions }),
      })
      if (!res.ok) throw new Error('Failed to apply suggestions')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redirects'] })
    },
  })
}
